import React from "react";
import PersonOutline from "@material-ui/icons/PersonOutline";
import PhoneOutlined from "@material-ui/icons/PhoneOutlined";
import EventOutlined from "@material-ui/icons/EventOutlined";
import HomeOutlined from "@material-ui/icons/HomeOutlined";
import VerifiedUser from "@material-ui/icons/VerifiedUser";
import Lock from "@material-ui/icons/Lock";
import HowToRegOutlined from "@material-ui/icons/HowToRegOutlined";
import PolicyOutlined from "@material-ui/icons/PolicyOutlined";
import AssignmentIndOutlined from "@material-ui/icons/AssignmentIndOutlined";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import {
  AddressFields,
  EditButton,
  Field,
  SameAddress,
  Section,
  StepHeader,
  useTranslation
} from "./ui";

const features = [
  [
    Lock,
    "Bank-level Security",
    "Your information is encrypted and transmitted securely."
  ],
  [
    HowToRegOutlined,
    "Used for Verification Only",
    "Your SSN is used to verify your identity and complete the merchant account approval."
  ],
  [
    PolicyOutlined,
    "Trusted Processing Partner",
    "We use industry-standard security measures and never share or sell your information."
  ]
];

export default function Principal({
  application,
  errors,
  onApplicationTextChange,
  onCheckChange,
  resetSsn
}) {
  const t = useTranslation();
  const props = { application, errors, onApplicationTextChange };
  return (
    <>
      <StepHeader
        title="Principal Info"
        detail="This information is required to verify your identity and approve your merchant account so you can accept credit and debit cards."
      >
        Tell us about the primary owner or authorized representative.
      </StepHeader>
      <div className="merchant-grid">
        <Field
          {...props}
          name="firstName"
          label="First Name"
          required
          icon={PersonOutline}
        />
        <Field
          {...props}
          name="lastName"
          label="Last Name"
          required
          icon={PersonOutline}
        />
        <Field
          {...props}
          name="dateOfBirth"
          label="Date of Birth"
          placeholder="MM/DD/YYYY"
          required
          icon={EventOutlined}
          inputProps={{ inputMode: "numeric" }}
        />
        <Field
          {...props}
          name="homePhone"
          label="Mobile Phone Number"
          required
          icon={PhoneOutlined}
          inputProps={{ inputMode: "tel" }}
        />
      </div>
      <Section title="Home Address">
        <SameAddress
          application={application}
          name="residenceDifferent"
          onCheckChange={onCheckChange}
          label="Home address is the same as business address"
        />
        {application.residenceDifferent && (
          <div className="merchant-subfields">
            <AddressFields {...props} prefix="home" streetIcon={HomeOutlined} />
          </div>
        )}
      </Section>
      <section className="merchant-identity">
        <div className="merchant-identity-head">
          <VerifiedUser className="merchant-shield" aria-hidden="true" />
          <div>
            <h2>{t("Secure Identity Verification")}</h2>
            <p>
              {t(
                "To approve your merchant account to accept credit and debit cards, our processing partner must verify your identity. This is a standard requirement for all merchant accounts and helps prevent fraud and protect your business."
              )}
            </p>
          </div>
        </div>
        <ul className="merchant-features">
          {features.map(([Icon, title, text]) => (
            <li key={title}>
              <span className="merchant-feature-icon">
                <Icon aria-hidden="true" />
              </span>
              <div>
                <strong>{t(title)}</strong>
                <p>{t(text)}</p>
              </div>
            </li>
          ))}
        </ul>
        <div className="merchant-ssn">
          <h3>{t("Social Security Number (Required)")}</h3>
          <div className="merchant-ssn-body">
            {application.encryptedSocialSecurityNumber &&
            !application.socialSecurityNumber ? (
              <div className="merchant-saved merchant-full">
                <strong>{t("SSN saved securely")}</strong>
                <EditButton onClick={resetSsn} label="Change SSN" />
              </div>
            ) : (
              <>
                <Field
                  {...props}
                  name="socialSecurityNumber"
                  label="Social Security Number"
                  required
                  secret
                  icon={AssignmentIndOutlined}
                  placeholder="XXX-XX-XXXX"
                  inputProps={{ inputMode: "numeric" }}
                  autoComplete="off"
                />
                <Field
                  {...props}
                  name="confirmSocialSecurityNumber"
                  label="Confirm Social Security Number"
                  required
                  secret
                  icon={AssignmentIndOutlined}
                  placeholder="XXX-XX-XXXX"
                  inputProps={{ inputMode: "numeric" }}
                  autoComplete="off"
                />
              </>
            )}
          </div>
        </div>
        <p className="merchant-why">
          <InfoOutlined aria-hidden="true" />
          <span>
            <strong>{t("Why is this required?")}</strong>{" "}
            {t(
              "When your business accepts credit cards, financial institutions are required to verify the identity of the individuals who own or control the business. Your personal information helps us complete this verification and approve your merchant account."
            )}
          </span>
        </p>
      </section>
    </>
  );
}
